
import { useEffect } from 'react';

interface PreloadResource {
  href: string;
  as: 'image' | 'font' | 'script' | 'style';
  type?: string;
  crossOrigin?: 'anonymous' | 'use-credentials';
  priority?: 'high' | 'low' | 'auto';
}

interface PreloadManagerProps {
  resources: PreloadResource[];
  disabled?: boolean;
}

const PreloadManager: React.FC<PreloadManagerProps> = ({ resources, disabled = false }) => {
  useEffect(() => {
    if (disabled || typeof window === 'undefined') return;

    // Only preload on fast connections
    const connection = (navigator as any).connection;
    if (connection && (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g')) {
      return;
    }

    const preloadedLinks: HTMLLinkElement[] = [];
    
    resources.forEach((resource) => {
      // Check if resource is already preloaded
      const existingLink = document.querySelector(`link[href="${resource.href}"]`);
      if (existingLink) return;

      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = resource.href;
      link.as = resource.as;
      
      if (resource.type) {
        link.type = resource.type;
      }
      
      if (resource.crossOrigin) {
        link.crossOrigin = resource.crossOrigin;
      }

      if (resource.priority) {
        link.setAttribute('fetchpriority', resource.priority);
      }
      
      // Add error handling
      link.onerror = () => {
        console.warn(`Failed to preload resource: ${resource.href}`);
      };
      
      document.head.appendChild(link);
      preloadedLinks.push(link);
    });

    // Cleanup function to remove preload links
    return () => {
      preloadedLinks.forEach((link) => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      });
    };
  }, [resources, disabled]);

  return null;
};

// Optimized hook for critical resource preloading
export const useCriticalResourcePreload = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Check network conditions
    const connection = (navigator as any).connection;
    const isFastConnection = !connection || 
      (connection.effectiveType !== 'slow-2g' && connection.effectiveType !== '2g');

    if (!isFastConnection) return;

    const criticalResources: PreloadResource[] = [
      // Only preload critical fonts
      {
        href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
        as: 'style',
        priority: 'high'
      },
      // Only preload hero image
      {
        href: 'https://lhfezfmidegmuwkdwjzt.supabase.co/storage/v1/object/public/img/LOGOS/Logo%20V1%20-%20Punto%20hispano',
        as: 'image',
        priority: 'high'
      }
    ];

    const preloadedLinks: HTMLLinkElement[] = [];

    criticalResources.forEach((resource) => {
      // Avoid duplicates
      if (document.querySelector(`link[href="${resource.href}"]`)) return;

      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = resource.href;
      link.as = resource.as;
      
      if (resource.type) {
        link.type = resource.type;
      }

      if (resource.priority) {
        link.setAttribute('fetchpriority', resource.priority);
      }
      
      document.head.appendChild(link);
      preloadedLinks.push(link);
    });

    // Cleanup
    return () => {
      preloadedLinks.forEach((link) => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      });
    };
  }, []);
};

export default PreloadManager;
